import { css, cx, mediaQueries } from '@eti/styles';
import { Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { XCircle } from '@phosphor-icons/react';
import { Icon } from '@eti/components';
import { useTranslation } from '@eti/providers';

const baggageSectionStyles = css`
  flex-basis: 0;
  flex-grow: 2;

  @media ${mediaQueries.small.only} {
    width: 100%;
  }
`;

const notPossibleToAddBaggageStyles = (
  isDividerOrResultCardBaggageEnabled: boolean,
  isInDrawer?: boolean,
  isIncludedSectionEnabled = false,
) => css`
  border-top: ${isDividerOrResultCardBaggageEnabled && isIncludedSectionEnabled && isInDrawer
    ? `1px solid ${vars.colors.divider}`
    : 'none'};
  flex-basis: ${isDividerOrResultCardBaggageEnabled ? 'auto' : '0'};
  flex-grow: 3;
  padding-top: ${isDividerOrResultCardBaggageEnabled && isIncludedSectionEnabled && isInDrawer
    ? '16px'
    : '0'};

  @media ${mediaQueries.small.only} {
    ${isIncludedSectionEnabled && isInDrawer && `border-top: 1px solid ${vars.colors.divider}`};
    ${isIncludedSectionEnabled && isInDrawer && `padding-top: 16px;`};
  }
`;

const baggageIconAndInfoSectionStyles = css`
  display: flex;
  flex-wrap: wrap;
  padding-left: 24px;
  position: relative;
`;

const checkInBaggageInfoStyles = css`
  flex-grow: 2;
`;

const checkInBaggageInfoMessageStyles = css`
  width: 100%;
`;

const iconWrapperStyles = css`
  background-color: ${vars.colors.signal.information.tint};
  border-radius: 8px;
  padding: 4px;
`;

export type NotPossibleToAddBaggageType = {
  baggageIcon: string;
  text: string;
  isCheckedInBaggage: boolean;
};

interface NotPossibleToAddBaggageProps {
  hasLessThan2hSelfTransfer?: boolean;
  isDividerOrResultCardBaggageEnabled: boolean;
  isInDrawer: boolean;
  isIncludedSectionEnabled: boolean;
  isResultCardBaggageEnabled: boolean;
  notPossibleToAddBaggageList: NotPossibleToAddBaggageType[];
}

const NotPossibleToAddBaggage = ({
  hasLessThan2hSelfTransfer,
  isDividerOrResultCardBaggageEnabled,
  isInDrawer,
  isIncludedSectionEnabled,
  isResultCardBaggageEnabled,
  notPossibleToAddBaggageList,
}: NotPossibleToAddBaggageProps) => {
  const { t } = useTranslation();

  return (
    <Stack
      className={cx(
        baggageSectionStyles,
        notPossibleToAddBaggageStyles(
          isDividerOrResultCardBaggageEnabled,
          isInDrawer,
          isIncludedSectionEnabled,
        ),
      )}
      data-testid="not-possible-to-add-section"
      spacing={8}
    >
      <Inline align="start" alignY="center" noWrap spacing={4}>
        <XCircle color="#000" size="20" weight="light" />
        <Text variant={textVariants._14_BOLD}>{t('Result.Baggage.NotPossibleToAdd.Text')}</Text>
      </Inline>

      {notPossibleToAddBaggageList.map(
        ({ baggageIcon, text, isCheckedInBaggage }, index: number) => {
          return (
            <Inline
              align="start"
              alignY="center"
              className={baggageIconAndInfoSectionStyles}
              key={`not-possible-to-add-baggage-${index}`}
              noWrap
              spacing={8}
            >
              <Icon
                className={isResultCardBaggageEnabled ? iconWrapperStyles : ''}
                id={baggageIcon}
                size="20px"
              />
              <Text
                className={checkInBaggageInfoStyles}
                data-testid={`not-possible-to-add-${text}`}
              >
                {text}
              </Text>
              {isCheckedInBaggage && !hasLessThan2hSelfTransfer && (
                <Text className={checkInBaggageInfoMessageStyles}>
                  {t('Result.Baggage.AddBaggageDuringCheckInOrAtAirport.Text')}
                </Text>
              )}
            </Inline>
          );
        },
      )}
    </Stack>
  );
};

export default NotPossibleToAddBaggage;
